import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { toSuggestion, type Suggestion } from './recurringWire'
import { LOAD_FAILED } from './suggestionsCopy'

/** The page the server is asked for: S2's `page_size=50`. */
const PAGE_SIZE = 50

type Page = { suggestions: Suggestion[]; total: number; hasMore: boolean; page: number }

async function fetchPage(page: number, pageSize: number, signal: AbortSignal): Promise<Page> {
  const { data, error } = await api.GET('/api/v1/recurring-suggestions', {
    params: { query: { status: 'pending', page, page_size: pageSize } },
    signal,
  })
  if (error !== undefined || data === undefined) throw new Error('recurring-suggestions-unavailable', { cause: error })
  return { suggestions: data.suggestions.map(toSuggestion), total: data.total, hasMore: data.has_more, page }
}

export type SuggestionsResult = {
  state: AsyncState<Suggestion[]>
  hasMore: boolean
  loadingMore: boolean
  loadMore: () => void
  /** Refetch every loaded page; resolves to the server's pending total (null if it failed). */
  refetch: () => Promise<number | null>
}

/**
 * The pending suggestions (M7-R18). A GET and nothing else: detection is the
 * user's own Look again (S1), never a side effect of opening the screen.
 * Pages accumulate under one key, so one invalidation refetches them all, and
 * the rows are deduped by id so a row shifting across a page boundary mid-list
 * cannot appear twice.
 */
export function useSuggestions(): SuggestionsResult {
  const query = useInfiniteQuery({
    queryKey: keys.recurring.suggestionsPending(),
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) => fetchPage(pageParam, PAGE_SIZE, signal),
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
  })
  const rows = query.data
    ? [...new Map(query.data.pages.flatMap((p) => p.suggestions).map((s) => [s.id, s])).values()]
    : undefined
  const state = fromQuery(
    { data: rows, isPending: query.isPending, isError: query.isError, error: query.error },
    {
      message: () => LOAD_FAILED,
      recovery: { label: 'Try again', onAct: () => void query.refetch() },
      isEmpty: (r) => r.length === 0,
    },
  )
  return {
    state,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
    refetch: async () => {
      const result = await query.refetch()
      return result.data?.pages[0]?.total ?? null
    },
  }
}

/**
 * The Suggestions button's badge: the server's pending total.
 *
 * **An errored count is `null` on purpose.** The badge is garnish on a link
 * that works without it, so a failed read says nothing rather than putting an
 * error on the list's header; the next invalidation retries it. A separate
 * one-row read, not the pending list's first page, so the list screen's page
 * size and the badge's cost stay independent.
 */
export function useSuggestionCount(): number | null {
  const query = useQuery({
    queryKey: keys.recurring.suggestionsCount(),
    queryFn: async ({ signal }) => (await fetchPage(1, 1, signal)).total,
  })
  return query.data ?? null
}
